import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Calendar, Loader2, AlertCircle, FileText } from 'lucide-react';
import { eventsService, State, District } from '../services/eventsService';
import { musterCountService, DailyMusterCount } from '../services/musterCountService';
import { MusterCountChart } from '../components/dashboard/MusterCountChart';
import { MusterCountTable } from '../components/dashboard/MusterCountTable';

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

// Get current year and month
const getCurrentYear = () => new Date().getFullYear();
const getCurrentMonth = () => new Date().getMonth() + 1; // getMonth() returns 0-11, so add 1

// Generate years array (always start from 2025, then next years)
const getYears = () => {
    const currentYear = getCurrentYear();
    const startYear = 2025;
    const endYear = Math.max(currentYear + 1, startYear + 1); // At least show 2025 and 2026

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
        years.push(year);
    }
    return years;
};

const MusterCount: React.FC = () => {
    const [selectedState, setSelectedState] = useState<number | null>(null);
    const [selectedDistrict, setSelectedDistrict] = useState<number | null>();
    const [selectedYear, setSelectedYear] = useState<number>(getCurrentYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(getCurrentMonth());
    const [years] = useState<number[]>(getYears());
    const [states, setStates] = useState<State[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dailyData, setDailyData] = useState<DailyMusterCount[]>([]);
    const [viewMode, setViewMode] = useState<'graph' | 'table'>('table');
    const hasInitialLoad = useRef(false);

    // Fetch states on mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                setLoadingStates(true);
                const statesData = await eventsService.getStates();
                setStates(statesData);
            } catch (error) {
                console.error('Error fetching states:', error);
                setStates([]);
            } finally {
                setLoadingStates(false);
            }
        };
        fetchStates();
    }, []);

    // Set first state as default when states are loaded and fetch data on initial load
    useEffect(() => {
        if (states.length > 0 && selectedState === null) {
            setSelectedState(states[0].id);
        }
    }, [states, selectedState]);

    // Fetch districts when state changes
    useEffect(() => {
        const fetchDistricts = async () => {
            if (!selectedState) {
                setDistricts([]);
                return;
            }

            try {
                setLoadingDistricts(true);
                const districtsData = await eventsService.getDistrictsByState(selectedState);
                setDistricts(districtsData);
            } catch (error) {
                console.error('Error fetching districts:', error);
                setDistricts([]);
            } finally {
                setLoadingDistricts(false);
            }
        };

        fetchDistricts();
    }, [selectedState]);

    // Set first district as default when districts are loaded
    useEffect(() => {
        if (selectedState && districts.length > 0) {
            // Only set district if it's not already set or if the current district is not in the available districts
            const currentDistrictValid = selectedDistrict && districts.some((d) => d.id === selectedDistrict);
            if (!currentDistrictValid) {
                setSelectedDistrict(districts[0].id);
            }
        } else {
            setSelectedDistrict(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [districts]);

    // Fetch data function
    const fetchData = async () => {
        if (!selectedState || !selectedYear || !selectedMonth || selectedDistrict === null || selectedDistrict === undefined) {
            return;
        }

        setError(null);
        setIsApplyingFilters(true);

        try {
            const data = await musterCountService.getDailyMusterCount({
                year: selectedYear,
                month: selectedMonth,
                stateId: selectedState,
                districtId: selectedDistrict,
            });

            setDailyData(data);
        } catch (error: any) {
            console.error('Error fetching muster count data:', error);
            setError(error?.message || 'Failed to fetch muster count data. Please try again.');
            setDailyData([]);
        } finally {
            setIsApplyingFilters(false);
        }
    };

    // Auto-fetch data once on initial load when all default values are set
    useEffect(() => {
        // Only fetch on initial load when state and district are set
        // districtId is mandatory, so wait for it to be set
        const shouldFetch = selectedState && selectedYear && selectedMonth && selectedDistrict !== null && selectedDistrict !== undefined && !hasInitialLoad.current && !loadingDistricts;

        if (shouldFetch) {
            hasInitialLoad.current = true;
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedState, selectedDistrict, loadingDistricts]);

    const handleApplyFilters = async () => {
        await fetchData();
    };

    const handleClearFilters = async () => {
        // Reset to current year and month
        const newState = states.length > 0 ? states[0].id : null;
        const newYear = getCurrentYear();
        const newMonth = getCurrentMonth();

        setSelectedState(newState);
        setSelectedYear(newYear);
        setSelectedMonth(newMonth);
        setError(null);
        hasInitialLoad.current = true; // Set to true to prevent auto-fetch, we'll call manually

        // Fetch districts for the new state and set the first district, then fetch data
        if (newState) {
            try {
                setIsApplyingFilters(true);
                const newStateDistricts = await eventsService.getDistrictsByState(newState);
                setDistricts(newStateDistricts);

                // Set the first district if available
                const newDistrict = newStateDistricts.length > 0 ? newStateDistricts[0].id : null;
                setSelectedDistrict(newDistrict);

                // Fetch data with the updated values
                if (newDistrict !== null && newDistrict !== undefined) {
                    try {
                        const data = await musterCountService.getDailyMusterCount({
                            year: newYear,
                            month: newMonth,
                            stateId: newState,
                            districtId: newDistrict,
                        });
                        setDailyData(data);
                    } catch (error: any) {
                        console.error('Error fetching muster count data:', error);
                        setError(error?.message || 'Failed to fetch muster count data. Please try again.');
                        setDailyData([]);
                    }
                } else {
                    setDailyData([]);
                }
            } catch (error) {
                console.error('Error fetching districts:', error);
                setDistricts([]);
                setSelectedDistrict(null);
                setDailyData([]);
            } finally {
                setIsApplyingFilters(false);
            }
        } else {
            setDistricts([]);
            setSelectedDistrict(null);
            setDailyData([]);
        }
    };

    // Calculate total muster count
    const totalMusterCount = dailyData.reduce((sum, item) => sum + item.musterRollCount, 0);

    return (
        <div className="space-y-6">
            {/* Filters Section */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <Card className="p-4 sm:p-6">
                    <div className="flex flex-col gap-4">
                        {error && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
                                <AlertCircle className="w-5 h-5" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}
                        <div className="flex flex-col lg:flex-row gap-4 items-end">
                            {/* State Filter */}
                            <div className="w-full lg:w-[250px] flex-shrink-0">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <MapPin className="w-4 h-4 inline mr-2" />
                                    State
                                </label>
                                <select
                                    value={selectedState || ''}
                                    onChange={(e) => setSelectedState(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent"
                                    disabled={loadingStates}
                                >
                                    {states.map((state) => (
                                        <option key={state.id} value={state.id}>
                                            {state.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* District Filter */}
                            <div className="w-full lg:w-[250px] flex-shrink-0">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <MapPin className="w-4 h-4 inline mr-2" />
                                    District
                                </label>
                                <select
                                    value={selectedDistrict || ''}
                                    onChange={(e) => setSelectedDistrict(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent"
                                    disabled={!selectedState || loadingDistricts || districts.length === 0}
                                >
                                    <option value="">All Districts</option>
                                    {districts.map((district) => (
                                        <option key={district.id} value={district.id}>
                                            {district.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Year Filter */}
                            <div className="w-full lg:w-[200px] flex-shrink-0">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Calendar className="w-4 h-4 inline mr-2" />
                                    Year
                                </label>
                                <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent">
                                    {years.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Month Filter */}
                            <div className="w-full lg:w-[200px] flex-shrink-0">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                                <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand focus:border-transparent">
                                    {MONTHS.map((month) => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Apply and Clear Buttons */}
                        <div className="flex flex-row gap-3 w-full lg:w-auto">
                            <Button onClick={handleApplyFilters} disabled={isApplyingFilters} className="h-10 bg-brand hover:bg-brand-hover text-white w-full lg:w-auto px-6">
                                {isApplyingFilters ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Applying...
                                    </>
                                ) : (
                                    'Apply Filter'
                                )}
                            </Button>
                            <Button onClick={handleClearFilters} variant="outline" className="h-10 w-full lg:w-auto px-6">
                                Clear
                            </Button>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Chart or Table Section */}
            {viewMode === 'graph' ? <MusterCountChart data={dailyData} viewMode={viewMode} onViewModeChange={setViewMode} /> : <MusterCountTable data={dailyData} viewMode={viewMode} onViewModeChange={setViewMode} isLoading={isApplyingFilters} />}
        </div>
    );
};

export default MusterCount;
